import {
    ColDef,
    ColumnPinnedEvent,
    ColumnState,
    GetMainMenuItems,
    GridApi,
    GridReadyEvent,
    ModelUpdatedEvent,
    RowDataUpdatedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { forwardRef } from "react";
import { ExperiencePurchaseItem } from "types";
import { DashboardTableGroupHeader } from "../DashboardTableGroupHeader";
import { refreshCell } from "../Roster/RosterGuestList";
import { GuestRowData, SummaryData } from "../Roster/RosterGuestTypes";
import { GroupedTableWrapper } from "./DashboardAgGridWrapper";
import { applyFlexToLastVisible, redrawModifiedRow, redrawSummaryRow, setTableHeight } from "./gridUtils";
import { useVisibleObserver } from "./tableHelpers";
import { useDashboardTableStore } from "./useDashboardTableStore";
import { useDashboardFilterStore } from "@/stores/dashboardFilterStore";

interface DashboardTableGroupProps {
    group: {
        groupLabel: string;
        items: ExperiencePurchaseItem[];
        rowData: (GuestRowData | SummaryData)[];
        allAttendeesUnique: boolean;
        someAttendeesUnique: boolean;
    };
    tableContainerReferences: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
    localExpandMap: Record<string, boolean>;
    setLocalExpandMap: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    columnDefs: ColDef[];
    onColumnPinned?(event: ColumnPinnedEvent): void;
    getMainMenuItems: GetMainMenuItems;
}

export const DashboardTableGroup = forwardRef<AgGridReact, DashboardTableGroupProps>(
    (
        {
            columnDefs,
            group,
            tableContainerReferences,
            localExpandMap,
            setLocalExpandMap,
            onColumnPinned,
            getMainMenuItems,
        },
        gridRef,
    ) => {
        const { visibleMap, observe } = useVisibleObserver();

        if (!group?.items || !Array.isArray(group.items)) {
            return null;
        }

        const updateTableHeight = (api: GridApi, groupLabel: string) => {
            setTableHeight(api, tableContainerReferences.current[groupLabel]);
        };

        const onModelDataUpdated = (params: ModelUpdatedEvent) => {
            updateTableHeight(params.api, group.groupLabel);
        };

        const isVisibleTable = visibleMap[group.groupLabel];

        return (
            <div
                key={group.groupLabel}
                ref={(element) => {
                    if (element) {
                        tableContainerReferences.current[group.groupLabel] = element;
                        observe(group.groupLabel, element);
                    }
                }}
                className="ag-theme-alpine ui-roster-guest-list dashboard-table mt-6 mb-7 h-full px-6 xl:mt-10 xl:px-10"
            >
                {isVisibleTable ? (
                    <>
                        <DashboardTableGroupHeader purchaseItems={group.items} groupId={group.groupLabel} />

                        <GroupedTableWrapper
                            ref={gridRef}
                            rowData={group.rowData}
                            columnDefs={columnDefs}
                            context={{
                                allAttendeesUnique: group.allAttendeesUnique,
                                someAttendeesUnique: group.someAttendeesUnique,
                                showExpandCollapseHeader: true,
                                groupLabel: group.groupLabel,
                                isExpanded: localExpandMap[group.groupLabel],
                                setExpanded: (expanded: boolean) => {
                                    setLocalExpandMap((previous) => ({
                                        ...previous,
                                        [group.groupLabel]: expanded,
                                    }));
                                },
                            }}
                            getMainMenuItems={getMainMenuItems}
                            onColumnPinned={onColumnPinned}
                            onRowDataUpdated={onRowDataUpdated}
                            onGridReady={onGridReady}
                            onRowGroupOpened={(event) => {
                                setTableHeight(event.api, tableContainerReferences.current[group.groupLabel]);
                                refreshCell(event);
                            }}
                            onFirstDataRendered={(event) => {
                                expandSummaryRowsAndAddFlex(event.api);
                                updateTableHeight(event.api, group.groupLabel);
                            }}
                            onModelUpdated={onModelDataUpdated}
                        />
                    </>
                ) : null}
            </div>
        );
    },
);

const onGridReady = (params: GridReadyEvent) => {
    const expandAllRows = useDashboardFilterStore.getState().displayOptions.expandAllRows;
    const columnState = useDashboardTableStore.getState().columnState;

    const { api } = params;
    if (expandAllRows) {
        api.expandAll();
    } else {
        api.collapseAll();
    }

    if (columnState && columnState.length > 0) {
        api.applyColumnState({ state: columnState, applyOrder: true });
    } else {
        const columnState = api.getColumns();
        const newColumnStates: ColumnState[] = [];
        if (columnState) {
            for (const column of columnState) {
                if (column) {
                    newColumnStates.push({
                        colId: column.getColId(),
                        hide: !column.isVisible(),
                    });
                }
            }
        }
        useDashboardTableStore.getState().actions.setColumnState(newColumnStates);
    }
};

export const expandSummaryRowsAndAddFlex = (api: GridApi) => {
    const showSummary = useDashboardFilterStore.getState().displayOptions.showSummaryRows;
    const expandAllRows = useDashboardFilterStore.getState().displayOptions.expandAllRows;

    if (showSummary && expandAllRows) {
        const summaryNode = api.getDisplayedRowAtIndex(0);
        if (summaryNode) {
            summaryNode.expanded = true;
            redrawSummaryRow(api, summaryNode);
        }
    }
    if (api.getAllDisplayedColumns().length < 18) {
        const columnState = api.getColumnState();
        const stateWithFlex = applyFlexToLastVisible(columnState);
        api.applyColumnState({ state: stateWithFlex, applyOrder: true });
    }
};

const onRowDataUpdated = (params: RowDataUpdatedEvent) => {
    redrawModifiedRow(params.api);
};
